import { useEffect, useMemo, useState } from "react"

import { EventType, PublicClientApplication } from "@azure/msal-browser"
import { MsalProvider } from "@azure/msal-react"
import { useTranslation } from "react-i18next"

import type { AccountInfo } from "@azure/msal-browser"

import { authSetupStatus, msalConfig, useLogin } from "@/api/auth-api"
import { LoadingSection } from "@/components/loading-section"
import { logger } from "@/lib/logger"

import { captureM365AuthorizationResponse } from "../utils/m365-connection"
import { isPopulatedMsalAuth } from "../utils/msal-config-validator"
import { capturePersonalSourceAuthorizationResponse } from "../utils/personal-source-callback"

// Minimal MSAL config for stub instance when MSAL auth isn't enabled.
// Uses a clearly-flagged sentinel that the validator rejects, so this stub can never
// accidentally drive a real login redirect to login.microsoftonline.com.
const stubMsalConfig = {
  auth: {
    clientId: "stub-client-id",
    authority: "https://login.microsoftonline.com/common",
    redirectUri: "/",
  },
}

export function MicrosoftAuthProvider({ children }: { children: React.ReactNode }) {
  // Only initialize real MSAL when login is enabled AND the backend returned a usable config.
  // Otherwise we run with the stub instance (so useMsal() hooks don't crash) and either render
  // a "Backend starting" panel (when the backend was unreachable) or render children normally
  // (legitimately login-disabled deploys).
  const { t } = useTranslation()
  const backendOk = authSetupStatus === "ok"
  const enabled = useLogin && backendOk && isPopulatedMsalAuth(msalConfig.auth)
  const [isInitialized, setIsInitialized] = useState(false)
  // The backend owns this consent code; remove it before either MSAL handler runs.
  useState(() => {
    captureM365AuthorizationResponse()
    capturePersonalSourceAuthorizationResponse()
    return true
  })

  const msalInstance = useMemo(() => {
    const config = enabled ? msalConfig : stubMsalConfig
    return new PublicClientApplication(config)
  }, [enabled])

  useEffect(() => {
    let cancelled = false

    async function initMsal() {
      try {
        // MSAL v3+ requires explicit initialization before any API calls
        await msalInstance.initialize()

        if (cancelled) return

        // Only process redirects if MSAL auth is enabled
        if (enabled) {
          // Process any pending redirect response (required for redirect-based flows).
          const result = await msalInstance.handleRedirectPromise()

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (cancelled) return

          if (result?.account) {
            msalInstance.setActiveAccount(result.account)
          } else {
            // Default to using the first account if no account is active
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
              msalInstance.setActiveAccount(accounts[0])
            }
          }
        }

        setIsInitialized(true)
      } catch (error) {
        logger.error("MSAL initialization failed", error as Error)
        // Still mark as initialized so the app can proceed (will show login)
        if (!cancelled) setIsInitialized(true)
      }
    }

    void initMsal()

    // Set up event listener for login success (only if MSAL auth is enabled)
    let callbackId: string | null = null
    if (enabled) {
      callbackId = msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const account = event.payload as AccountInfo
          msalInstance.setActiveAccount(account)
        }
      })
    }

    return () => {
      cancelled = true
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId)
      }
    }
  }, [enabled, msalInstance])

  // Show loading while MSAL initializes
  if (!isInitialized) {
    return (
      <section className="flex h-screen w-screen items-center justify-center">
        <LoadingSection />
      </section>
    )
  }

  // Backend never returned a usable MSAL config (e.g. dev backend still warming up).
  // Show an actionable error instead of letting the app fall through and trigger a redirect
  // with placeholder credentials. Note: useLogin is forced to false in the unavailable case
  // (see backendUnavailableSetup in auth-api.ts), so we gate purely on backendOk here.
  if (!backendOk) {
    return (
      <section className="flex h-screen w-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-semibold">{t("auth.backendNotReady.title")}</h1>
        <p className="text-muted-foreground max-w-md text-sm">
          {t("auth.backendNotReady.description")}
        </p>
        <button
          className="hover:bg-muted rounded border px-4 py-2 text-sm"
          type="button"
          onClick={() => window.location.reload()}
        >
          {t("auth.backendNotReady.retry")}
        </button>
      </section>
    )
  }

  // Always wrap with MsalProvider so useMsal() hooks work throughout the app
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>
}
